import { useQuery, UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { client } from './apiClient';
import {
  BookmarkCollectionResponse,
  BookmarkResponse,
  BookmarkContentResponse,
  ProfileData,
  Bookmark,
  HighlightsResponse,
  Note,
} from '../types/api';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { getHomeBookmarksParams } from './bookmarkCache';

// Cache durations in milliseconds
const CACHE_TIMES = {
  BOOKMARKS: 2 * 60 * 1000, // 2 minutes
  BOOKMARK: 5 * 60 * 1000, // 5 minutes
  CONTENT: 10 * 60 * 1000, // 10 minutes
  KEY_SENTENCES: 30 * 60 * 1000, // 30 minutes
} as const;

const DEFAULT_RETRY = 1;

// Result types for convenience hash returns
export interface UseBookmarksResult {
  bookmarks: Bookmark[];
  hasMore: boolean;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: UseQueryResult<BookmarkCollectionResponse>['refetch'];
  isFetching: boolean;
  isSuccess: boolean;
}

export interface UseBookmarkResult {
  bookmark: Bookmark | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: UseQueryResult<BookmarkResponse>['refetch'];
  isFetching: boolean;
  isSuccess: boolean;
}

export interface UseBookmarkContentResult {
  content: BookmarkContentResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: UseQueryResult<BookmarkContentResponse>['refetch'];
  isFetching: boolean;
  isSuccess: boolean;
}

export interface UseKeySentencesResult {
  keySentences: string[] | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: UseQueryResult<string[]>['refetch'];
  isFetching: boolean;
  isSuccess: boolean;
}

export interface UseUserProfileResult {
  profile: ProfileData | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: UseQueryResult<ProfileData>['refetch'];
  isFetching: boolean;
  isSuccess: boolean;
}

function useBookmarks(
  params: Parameters<typeof client.bookmarks>[0],
): UseBookmarksResult {
  const { data, isLoading, error, isError, refetch, isFetching, isSuccess } =
    useQuery<BookmarkCollectionResponse>({
      queryKey: ['bookmarks', params],
      queryFn: () => client.bookmarks(params),
      staleTime: CACHE_TIMES.BOOKMARKS,
      retry: DEFAULT_RETRY,
    });

  const bookmarks = data?.bookmarks || [];
  const hasMore = data?.has_more ?? false;

  return {
    bookmarks,
    hasMore,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  };
}

function useBookmark(bookmarkId: string): UseBookmarkResult {
  const queryClient = useQueryClient();

  // Normalize bookmarkId to always be a string to avoid query key mismatches
  const normalizedBookmarkId = String(bookmarkId);

  // Try to find the bookmark in cached bookmark list data (any section/sort)
  const findCachedBookmark = (): Bookmark | undefined => {
    const bookmarkListQueries = queryClient.getQueriesData<BookmarkCollectionResponse>({
      queryKey: ['bookmarks'],
    });
    for (const [, cachedData] of bookmarkListQueries) {
      if (cachedData?.bookmarks) {
        const foundBookmark = cachedData.bookmarks.find(
          (b) => b.id.toString() === normalizedBookmarkId,
        );
        if (foundBookmark) {
          return foundBookmark;
        }
      }
    }
    return undefined;
  };

  const cachedBookmark = findCachedBookmark();

  const { data, isLoading, error, isError, refetch, isFetching, isSuccess } =
    useQuery<BookmarkResponse>({
      queryKey: ['bookmark', normalizedBookmarkId],
      queryFn: () => client.bookmark(normalizedBookmarkId),
      staleTime: CACHE_TIMES.BOOKMARK,
      retry: DEFAULT_RETRY,
      // Use cached bookmark as initial data for instant display
      initialData: cachedBookmark ? { bookmark: cachedBookmark } : undefined,
    });

  const bookmark = data?.bookmark;

  return {
    bookmark,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  };
}

function useBookmarkContent(bookmarkId: string): UseBookmarkContentResult {
  const { data, isLoading, error, isError, refetch, isFetching, isSuccess } =
    useQuery<BookmarkContentResponse>({
      queryKey: ['bookmarkContent', bookmarkId],
      queryFn: () => client.bookmarkContent(bookmarkId),
      staleTime: CACHE_TIMES.CONTENT,
      retry: DEFAULT_RETRY,
    });

  return {
    content: data,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  };
}

function useHomeBookmarks(page = 1): UseBookmarksResult {
  const { preferences } = useUserPreferences();
  return useBookmarks(getHomeBookmarksParams(page, preferences.bookmarkList.sortOrder));
}

function useLikedBookmarks(page = 1): UseBookmarksResult {
  const { preferences } = useUserPreferences();
  return useBookmarks({
    section_type: 'liked',
    page,
    sort: preferences.bookmarkList.sortOrder,
  });
}

function useArchivedBookmarks(page = 1): UseBookmarksResult {
  const { preferences } = useUserPreferences();
  return useBookmarks({
    section_type: 'archive',
    page,
    sort: preferences.bookmarkList.sortOrder,
  });
}

function useFolderBookmarks(
  folderId: string,
  page = 1,
  includeUser = false,
): UseBookmarksResult {
  const { preferences } = useUserPreferences();
  return useBookmarks({
    section_type: 'folder',
    folder_id: folderId,
    page,
    include_user: includeUser,
    sort: preferences.bookmarkList.sortOrder,
  });
}

function useTagBookmarks(tag: string, page = 1): UseBookmarksResult {
  const { preferences } = useUserPreferences();
  return useBookmarks({
    section_type: 'tag',
    tag,
    page,
    sort: preferences.bookmarkList.sortOrder,
  });
}

function useVideoBookmarks(page = 1, includeUser = false): UseBookmarksResult {
  const { preferences } = useUserPreferences();
  return useBookmarks({
    section_type: 'videos',
    page,
    include_user: includeUser,
    sort: preferences.bookmarkList.sortOrder,
  });
}

function useUserProfile(userId: string, page = 1): UseUserProfileResult {
  const { data, isLoading, error, isError, refetch, isFetching, isSuccess } =
    useQuery<ProfileData>({
      queryKey: ['profile', userId, page],
      queryFn: () => client.profile(userId, page),
      staleTime: 1000 * 60 * 5,
      retry: 1,
    });

  return {
    profile: data,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  };
}

function useKeySentences(
  bookmarkId: string,
  text: string,
  enabled = false,
): UseKeySentencesResult {
  const { data, isLoading, error, isError, refetch, isFetching, isSuccess } = useQuery<
    string[]
  >({
    queryKey: ['keySentences', bookmarkId, text],
    queryFn: () => client.keySentences(bookmarkId, text),
    enabled,
    staleTime: CACHE_TIMES.KEY_SENTENCES,
    retry: DEFAULT_RETRY,
  });

  return {
    keySentences: data,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  };
}

// Result type for highlights
export interface UseHighlightsResult {
  notes: Note[];
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: UseQueryResult<HighlightsResponse>['refetch'];
  isFetching: boolean;
  isSuccess: boolean;
}

function useHighlights(page = 1, includeUser = false): UseHighlightsResult {
  const { data, isLoading, error, isError, refetch, isFetching, isSuccess } = useQuery({
    queryKey: ['highlights', page, includeUser],
    queryFn: () => client.highlights({ page, include_user: includeUser }),
    staleTime: CACHE_TIMES.BOOKMARKS,
    retry: DEFAULT_RETRY,
  });

  return {
    notes: data?.notes || [],
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  };
}

export const BOOKMARKS_CACHE_TIME = CACHE_TIMES.BOOKMARKS;

export {
  useBookmarks,
  useBookmark,
  useBookmarkContent,
  useKeySentences,
  useHomeBookmarks,
  useLikedBookmarks,
  useArchivedBookmarks,
  useFolderBookmarks,
  useTagBookmarks,
  useVideoBookmarks,
  useUserProfile,
  useHighlights,
};
